import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUp, ChevronDown } from "lucide-react";
import { Context } from "../../context/Context";
import { getLegalDocument, LEGAL_DOCUMENTS, LEGAL_UI_TEXT } from "./content";
import "./LegalDocument.css";

const LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g;

const scrollBehavior = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

// Turns "text [label](href) text" into text and links.
function InlineText({ text }) {
  const parts = [];
  let last = 0;
  for (const match of text.matchAll(LINK_PATTERN)) {
    parts.push(text.slice(last, match.index));
    const [whole, label, href] = match;
    parts.push(
      href.startsWith("/") ? (
        <Link key={match.index} to={href} className="legal-link">{label}</Link>
      ) : (
        <a key={match.index} href={href} className="legal-link">{label}</a>
      ),
    );
    last = match.index + whole.length;
  }
  parts.push(text.slice(last));
  return parts.map((part, i) => <Fragment key={i}>{part}</Fragment>);
}

function Block({ block, locale }) {
  switch (block.type) {
    case "paragraph":
      return <p><InlineText text={block.text[locale]} /></p>;
    case "subheading":
      return <h3 id={block.id} tabIndex={-1}>{block.text[locale]}</h3>;
    case "list":
      return (
        <ul>
          {block.items.map((item, i) => (
            <li key={i}><InlineText text={item[locale]} /></li>
          ))}
        </ul>
      );
    case "definitions":
      return (
        <dl className="legal-definitions">
          {block.items.map((item, i) => (
            <div key={i}>
              <dt>{item.term[locale]}</dt>
              <dd><InlineText text={item.description[locale]} /></dd>
            </div>
          ))}
        </dl>
      );
    default:
      return null;
  }
}

export default function LegalDocument({ documentId }) {
  const { lang } = useContext(Context);
  const locale = lang === "ar" ? "ar" : "en";
  const ui = LEGAL_UI_TEXT[locale];
  const doc = getLegalDocument(documentId);
  const tocSections = doc.sections.filter((section) => section.title);

  const location = useLocation();
  const navigate = useNavigate();
  const titleRef = useRef(null);
  const [tocOpen, setTocOpen] = useState(false);
  const [activeId, setActiveId] = useState(tocSections[0]?.id);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const hasNavigated = useRef(false);

  const scrollToTarget = useCallback((id, behavior, moveFocus) => {
    const target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior, block: "start" });
    if (moveFocus) (target.querySelector("h2") || target).focus?.({ preventScroll: true });
  }, []);

  // Deep links and contents links go through the URL, so refresh and browser back keep the position.
  const hash = decodeURIComponent(location.hash.slice(1));
  useEffect(() => {
    const firstRender = !hasNavigated.current;
    hasNavigated.current = true;
    if (hash) {
      scrollToTarget(hash, firstRender ? "auto" : scrollBehavior(), !firstRender);
      setActiveId(hash);
    } else {
      window.scrollTo?.({ top: 0, behavior: "auto" });
    }
  }, [documentId, hash, scrollToTarget]);

  // The two languages differ in length: keep the reader on the same section when switching.
  const previousLocale = useRef(locale);
  useEffect(() => {
    if (previousLocale.current === locale) return;
    previousLocale.current = locale;
    if (window.scrollY > 200 && activeIdRef.current) scrollToTarget(activeIdRef.current, "auto", false);
  }, [locale, scrollToTarget]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length) setActiveId(visible[visible.length - 1].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );
    doc.sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element && section.title) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [doc]);

  useEffect(() => {
    const firstSectionId = doc.sections.find((section) => section.title)?.id;
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 500);
      // Above the first section nothing crosses the observer's band, so the highlight would go stale.
      if (window.scrollY < 120) setActiveId(firstSectionId);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [doc]);

  useEffect(() => {
    setTocOpen(false);
  }, [documentId]);

  const openSection = (event, id) => {
    event.preventDefault();
    setTocOpen(false);
    if (id === hash) scrollToTarget(id, scrollBehavior(), true);
    else navigate({ hash: id });
  };

  const backToTop = () => {
    if (hash) navigate({ hash: "" }, { replace: true });
    window.scrollTo?.({ top: 0, behavior: scrollBehavior() });
    titleRef.current?.focus({ preventScroll: true });
  };

  return (
    <main className="legal-page" dir={locale === "ar" ? "rtl" : "ltr"} lang={locale}>
      <div className="legal-shell">
        <header className="legal-header">
          <nav className="legal-switcher" aria-label={ui.documents}>
            {LEGAL_DOCUMENTS.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className="legal-switcher-link"
                aria-current={item.id === doc.id ? "page" : undefined}
              >
                {item.title[locale]}
              </Link>
            ))}
          </nav>
          <h1 ref={titleRef} tabIndex={-1}>{doc.title[locale]}</h1>
          {ui.translationNotice && <p className="legal-notice" role="note">{ui.translationNotice}</p>}
        </header>

        <div className={tocSections.length > 1 ? "legal-layout" : "legal-layout single"}>
          {tocSections.length > 1 && (
            <aside className="legal-toc">
              <button
                type="button"
                className="legal-toc-toggle"
                aria-expanded={tocOpen}
                aria-controls="legal-toc-list"
                onClick={() => setTocOpen((open) => !open)}
              >
                <span>{ui.contents}</span>
                <ChevronDown size={18} aria-hidden="true" className={tocOpen ? "open" : ""} />
              </button>
              <nav
                id="legal-toc-list"
                className={tocOpen ? "legal-toc-list open" : "legal-toc-list"}
                aria-label={ui.contents}
                onKeyDown={(event) => event.key === "Escape" && setTocOpen(false)}
              >
                <p className="legal-toc-heading">{ui.contents}</p>
                <ul>
                  {tocSections.map((section) => (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        aria-current={activeId === section.id ? "location" : undefined}
                        onClick={(event) => openSection(event, section.id)}
                      >
                        {section.title[locale]}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>
          )}

          <article className="legal-article">
            {doc.sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                aria-labelledby={section.title ? `${section.id}-title` : undefined}
              >
                {section.title && <h2 id={`${section.id}-title`} tabIndex={-1}>{section.title[locale]}</h2>}
                {section.blocks.map((block, i) => (
                  <Block key={i} block={block} locale={locale} />
                ))}
              </section>
            ))}
          </article>
        </div>
      </div>

      {showBackToTop && (
        <button type="button" className="legal-back-to-top" aria-label={ui.backToTop} title={ui.backToTop} onClick={backToTop}>
          <ArrowUp size={18} aria-hidden="true" />
        </button>
      )}
    </main>
  );
}
